import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Result, Button, Typography, Space, message, Tag, Spin } from 'antd';
import {
  HomeOutlined,
  BugOutlined,
  CopyOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import {
  resolveErrorSourceMap,
  formatFullErrorReport,
  MappedErrorInfo,
} from './sourceMapResolver';

const { Paragraph, Text } = Typography;

export interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode | ((props: { error: Error; resetError: () => void }) => ReactNode);
  title?: string;
  subTitle?: string;
  compact?: boolean;
  onReset?: () => void;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  mappedInfo: MappedErrorInfo | null;
  isResolvingSourceMap: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      mappedInfo: null,
      isResolvingSourceMap: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary caught error]:', error, errorInfo);
    this.setState({ errorInfo, isResolvingSourceMap: true });
    this.props.onError?.(error, errorInfo);

    // Resolve source map to map back to original TypeScript source code
    resolveErrorSourceMap(error, errorInfo?.componentStack)
      .then((mappedInfo) => {
        this.setState({ mappedInfo, isResolvingSourceMap: false });
      })
      .catch((err) => {
        console.warn('[ErrorBoundary] Failed to map sourcemap:', err);
        this.setState({ isResolvingSourceMap: false });
      });
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      mappedInfo: null,
      isResolvingSourceMap: false,
    });
    this.props.onReset?.();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  handleCopyText = (text: string, label: string = 'Nội dung'): void => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      message.success(`Đã sao chép ${label.toLowerCase()} vào bộ nhớ tạm`);
    } else {
      message.info('Không thể tự động sao chép');
    }
  };

  handleCopyErrorDetails = (): void => {
    const { error, errorInfo, mappedInfo } = this.state;
    const report = formatFullErrorReport({ error, errorInfo, mappedInfo });
    this.handleCopyText(report, 'chi tiết lỗi');
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, showDetails, mappedInfo, isResolvingSourceMap } = this.state;
    const { children, fallback, title, subTitle, compact } = this.props;

    if (!hasError) {
      return children;
    }

    // Custom fallback support
    if (fallback) {
      if (typeof fallback === 'function' && error) {
        return fallback({ error, resetError: this.resetError });
      }
      return fallback;
    }

    const defaultTitle = title || 'Đã xảy ra sự cố hiển thị';
    const defaultSubTitle =
      subTitle ||
      (error?.message
        ? `Lỗi: ${error.message}`
        : 'Hệ thống đã ghi nhận lỗi. Bạn có thể sao chép chi tiết lỗi để gửi cho đội ngũ phát triển.');

    const primaryLoc = mappedInfo?.primaryLocation;

    // Compact mode for small panels / widgets
    if (compact) {
      return (
        <CompactContainer>
          <CompactHeader>
            <BugOutlined style={{ color: '#ef4444', fontSize: 18 }} />
            <Text strong style={{ color: '#1f2937' }}>
              {defaultTitle}
            </Text>
          </CompactHeader>
          <CompactMessage type="secondary">{defaultSubTitle}</CompactMessage>
          <Space size="small" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            <Button
              size="small"
              type="primary"
              icon={<CopyOutlined />}
              onClick={this.handleCopyErrorDetails}
            >
              Sao chép chi tiết lỗi
            </Button>
            <Button size="small" onClick={this.toggleDetails}>
              {showDetails ? 'Ẩn chi tiết' : 'Chi tiết'}
            </Button>
          </Space>

          {showDetails && (
            <DetailsBlock>
              <ErrorHeaderBox>
                <div className="error-title">
                  <BugOutlined style={{ color: '#dc2626', fontSize: 14 }} />
                  <span>
                    {error?.name || 'Error'}: {error?.message}
                  </span>
                </div>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  title="Sao chép chi tiết lỗi"
                  onClick={this.handleCopyErrorDetails}
                />
              </ErrorHeaderBox>

              {/* Source code mapping location in compact mode */}
              {isResolvingSourceMap && (
                <LoadingMappingBox>
                  <Spin size="small" />
                  <span>Đang tải vị trí mã nguồn TypeScript...</span>
                </LoadingMappingBox>
              )}

              {primaryLoc && (
                <TypeScriptLocationBox>
                  <div className="location-header">
                    <TSBadge>TS</TSBadge>
                    <span className="location-title">Vị trí lỗi TypeScript:</span>
                  </div>
                  <div className="location-file">
                    {primaryLoc.originalFile}:{primaryLoc.originalLine}:{primaryLoc.originalColumn}
                    {primaryLoc.componentName && (
                      <span className="component-tag"> &lt;{primaryLoc.componentName} /&gt;</span>
                    )}
                  </div>
                  {primaryLoc.sourceSnippet && (
                    <SnippetCodeBlock>{primaryLoc.sourceSnippet}</SnippetCodeBlock>
                  )}
                </TypeScriptLocationBox>
              )}

              {(mappedInfo?.mappedStack || error?.stack) && (
                <div style={{ marginTop: 12 }}>
                  <SectionTitleRow>
                    <Text strong style={{ color: '#475569', fontSize: 11 }}>
                      Stack trace (Mapped):
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 11 }}
                      icon={<CopyOutlined />}
                      onClick={() =>
                        this.handleCopyText(mappedInfo?.mappedStack || error?.stack || '', 'Stack trace')
                      }
                    >
                      Sao chép
                    </Button>
                  </SectionTitleRow>
                  <StackTrace>{mappedInfo?.mappedStack || error?.stack}</StackTrace>
                </div>
              )}

              {(mappedInfo?.mappedComponentStack || errorInfo?.componentStack) && (
                <div style={{ marginTop: 12 }}>
                  <SectionTitleRow>
                    <Text strong style={{ color: '#475569', fontSize: 11 }}>
                      Component stack:
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 11 }}
                      icon={<CopyOutlined />}
                      onClick={() =>
                        this.handleCopyText(
                          mappedInfo?.mappedComponentStack || errorInfo?.componentStack || '',
                          'Component stack'
                        )
                      }
                    >
                      Sao chép
                    </Button>
                  </SectionTitleRow>
                  <StackTrace>
                    {mappedInfo?.mappedComponentStack || errorInfo?.componentStack}
                  </StackTrace>
                </div>
              )}
            </DetailsBlock>
          )}
        </CompactContainer>
      );
    }

    // Full / Page mode
    return (
      <FullContainer>
        <CardWrapper>
          <Result
            status="error"
            title={defaultTitle}
            subTitle={defaultSubTitle}
            extra={[
              <Button
                type="primary"
                key="copy-details"
                icon={<CopyOutlined />}
                onClick={this.handleCopyErrorDetails}
              >
                Sao chép chi tiết lỗi
              </Button>,
              <Button key="home" icon={<HomeOutlined />} onClick={this.handleGoHome}>
                Về trang chủ
              </Button>,
              <Button key="details" onClick={this.toggleDetails}>
                {showDetails ? 'Ẩn chi tiết lỗi' : 'Xem chi tiết lỗi'}
              </Button>,
            ]}
          >
            {showDetails && (
              <ErrorDetailsContainer>
                <ErrorHeaderBox>
                  <div className="error-title">
                    <BugOutlined style={{ color: '#dc2626', fontSize: 16 }} />
                    <span>
                      {error?.name || 'Error'}: {error?.message}
                    </span>
                  </div>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={this.handleCopyErrorDetails}
                  >
                    Sao chép chi tiết lỗi
                  </Button>
                </ErrorHeaderBox>

                {/* TypeScript Source Code Mapping Box */}
                {isResolvingSourceMap && (
                  <LoadingMappingBox>
                    <Spin size="small" />
                    <span>Đang giải mã vị trí TypeScript từ source map...</span>
                  </LoadingMappingBox>
                )}

                {primaryLoc && (
                  <TypeScriptLocationBox>
                    <div className="location-header">
                      <TSBadge>TS</TSBadge>
                      <span className="location-title">Vị trí phát sinh lỗi trong TypeScript:</span>
                      {primaryLoc.componentName && (
                        <Tag color="blue" icon={<CodeOutlined />}>
                          &lt;{primaryLoc.componentName} /&gt;
                        </Tag>
                      )}
                    </div>

                    <div className="location-file">
                      <CodeOutlined style={{ marginRight: 6, color: '#2563eb' }} />
                      <strong>{primaryLoc.originalFile}</strong>
                      <span style={{ color: '#64748b' }}>
                        :{primaryLoc.originalLine}:{primaryLoc.originalColumn}
                      </span>
                    </div>

                    {primaryLoc.sourceSnippet && (
                      <div style={{ marginTop: 10 }}>
                        <SnippetCodeBlock>{primaryLoc.sourceSnippet}</SnippetCodeBlock>
                      </div>
                    )}
                  </TypeScriptLocationBox>
                )}

                {/* Stack Trace (Mapped from TypeScript) */}
                {(mappedInfo?.mappedStack || error?.stack) && (
                  <div style={{ marginTop: 16 }}>
                    <SectionTitleRow>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Text strong style={{ color: '#334155', fontSize: 12 }}>
                          Stack trace:
                        </Text>
                        {mappedInfo?.mappedStack && (
                          <Tag color="cyan" style={{ fontSize: 10, lineHeight: '18px' }}>
                            TypeScript Mapped
                          </Tag>
                        )}
                      </div>
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          this.handleCopyText(
                            mappedInfo?.mappedStack || error?.stack || '',
                            'Stack trace'
                          )
                        }
                      >
                        Sao chép
                      </Button>
                    </SectionTitleRow>
                    <StackTrace>{mappedInfo?.mappedStack || error?.stack}</StackTrace>
                  </div>
                )}

                {/* React Component Stack */}
                {(mappedInfo?.mappedComponentStack || errorInfo?.componentStack) && (
                  <div style={{ marginTop: 14 }}>
                    <SectionTitleRow>
                      <Text strong style={{ color: '#334155', fontSize: 12 }}>
                        Component hierarchy stack:
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          this.handleCopyText(
                            mappedInfo?.mappedComponentStack || errorInfo?.componentStack || '',
                            'Component stack'
                          )
                        }
                      >
                        Sao chép
                      </Button>
                    </SectionTitleRow>
                    <StackTrace>
                      {mappedInfo?.mappedComponentStack || errorInfo?.componentStack}
                    </StackTrace>
                  </div>
                )}
              </ErrorDetailsContainer>
            )}
          </Result>
        </CardWrapper>
      </FullContainer>
    );
  }
}

export default ErrorBoundary;

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const ComponentWithErrorBoundary = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  ComponentWithErrorBoundary.displayName = `WithErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}

const FullContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 24px;
  box-sizing: border-box;
`;

const CardWrapper = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  padding: 32px 24px;
  max-width: 860px;
  width: 100%;
  box-sizing: border-box;
`;

const CompactContainer = styled.div`
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 8px 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const CompactMessage = styled(Paragraph)`
  margin: 0 !important;
  font-size: 13px;
  color: #4b5563;
`;

const DetailsBlock = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 380px;
  overflow-y: auto;
  font-size: 12px;
`;

const ErrorDetailsContainer = styled.div`
  text-align: left;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 20px;
  margin-top: 20px;
  max-height: 520px;
  overflow-y: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const ErrorHeaderBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  padding: 10px 14px;

  .error-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #dc2626;
    font-size: 13px;
    word-break: break-word;
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const StackTrace = styled.pre`
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const TypeScriptLocationBox = styled.div`
  margin-top: 14px;
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;

  .location-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }

  .location-title {
    font-size: 13px;
    font-weight: 600;
    color: #1e3a8a;
  }

  .location-file {
    font-size: 13px;
    color: #1e293b;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
    word-break: break-word;
  }

  .component-tag {
    color: #2563eb;
    font-weight: 600;
  }
`;

const TSBadge = styled.span`
  background: #2563eb;
  color: #ffffff;
  font-weight: 700;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

const LoadingMappingBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  color: #64748b;
  font-size: 12px;
`;

const SnippetCodeBlock = styled.pre`
  background: #1e293b;
  color: #f8fafc;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  margin: 6px 0 0 0;
  overflow-x: auto;
  white-space: pre;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

  /* Highlight target line marker */
  span.target-line {
    background: rgba(239, 68, 68, 0.25);
    display: block;
    border-left: 2px solid #ef4444;
    padding-left: 4px;
  }
`;
